import { CrudMode, ValidationFeedback } from '@ohpen/enso-ui';
import {
  ActionEntityType,
  FieldType,
  productChangesGetResult,
  ercProfilesGetResult,
  feeDefinitionsGetResult,
  feeProfilesGetResult,
  exchangeRatesGetResult,
  baseRatesGetResult,
  interestRatesGetResult,
  productExclusivitySetsGetResult,
  procurationFeeRulesGetResult,
  procurationFeesGetResult,
  procurationFeeAssignmentsGetResult,
  firmProcFeeAssignmentsGetResult,
  firmsGetResult,
  lendersGetResult,
  intermediaryGetResult,
  productChangesGetData,
  ercProfilesGetData,
  feeDefinitionsGetData,
  feeProfilesGetData,
  exchangeRatesGetData,
  baseRatesGetData,
  interestRatesGetData,
  productExclusivitySetsGetData,
  procurationFeeRulesGetData,
  procurationFeesGetData,
  procurationFeeAssignmentsGetData,
  firmProcFeeAssignmentsGetData,
  firmsGetData,
  lendersGetData,
  intermediaryGetData,
} from '@pmp/api';
import {
  BASE_RATES_VALUE_LIST_NAMES,
  BROKERS_VALUE_LIST_NAMES,
  EXCLUSIVE_SETS_ENTITY_LIST_NAMES,
  FEES_ENTITY_LIST_NAMES,
  FEES_VALUE_LIST_NAMES,
  FIRMS_ENTITY_LIST_NAMES,
  flattenEntityCrudAdditionalFields,
  flattenFirmAddress,
  flattenLenderDraft,
  flattenFirmProcFeeBoost,
  splitChangeResult,
  LENDER_ENTITY_LIST_NAMES,
  PROCURATION_FEES_ENTITY_LIST_NAMES,
  PROCURATION_FEES_VALUE_LIST_NAMES,
  PRODUCTS_ENTITY_LIST_NAMES,
  PRODUCTS_VALUE_LIST_NAMES,
  EXCHANGE_RATE_VALUE_LIST_NAMES,
  PROCURATION_FEES_LIST_NAMES,
} from '@pmp/components';
import { getApiErrorMessage } from '@pmp/utils';
import { EntityCrudData, OpenCrudProps } from './interfaces';
import { EntityChangeDataResponse, EntityChangeResultResponse, WithRowNumber } from '../../components/CrudTable/interfaces';
import { GetValueListsProps } from '@pmp/stores';

export const openCrud = <EntityChangeDataRow>({
  record,
  mode,
  getAdditionalFields,
  getValueLists,
  dispatch,
  notification,
  t,
}: OpenCrudProps) => {
  const entityType = record.actionEntityType.key as ActionEntityType | null;
  const actionId = record.actionId ?? '';

  if (!entityType) {
    notification().error({ title: t('global.errors.unexpected') });
    return;
  }
  dispatch({ type: 'openCrudTable', crudTable: { changeRequestData: record, entityType, actionId, isLoading: true, mode } });

  const toCrudData = (rows: Array<WithRowNumber<EntityChangeDataRow>>): EntityCrudData[] => {
    let data: EntityCrudData[] = flattenEntityCrudAdditionalFields<WithRowNumber<EntityChangeDataRow>, EntityCrudData>(rows);
    if (entityType === ActionEntityType.FIRMS) data = flattenFirmProcFeeBoost(flattenFirmAddress(data));
    if (entityType === ActionEntityType.LENDERS) data = flattenLenderDraft(data);
    return data;
  };

  const getChangesGetData = () => {
    return mode === CrudMode.VIEW_RESULT
      ? entityChangesGetResult<EntityChangeDataRow>(entityType, actionId).then(({ rows }) => {
          const { data, originalData, validationFeedback } = splitChangeResult(rows, toCrudData);

          dispatch({
            type: 'openCrudTable',
            entityType,
            crudTable: { changeRequestData: record, actionId, data, originalData, validationFeedback, isLoading: false, mode },
          });
        })
      : entityChangesGetData<EntityChangeDataRow>(entityType, actionId).then(({ rows, originalRows, validationFeedback, isValid, reviewInfo }) => {
          const data = toCrudData(rows);
          const originalData = toCrudData(originalRows);

          dispatch({
            type: 'openCrudTable',
            crudTable: {
              changeRequestData: record,
              entityType,
              actionId,
              data,
              originalData,
              reviewInfo,
              validationFeedback,
              isValid: isValid ?? false,
              isLoading: false,
              mode,
            },
          });
        });
  };
  getValueLists(getValueListNamesByEntityType(entityType))
    .then(() => getAdditionalFields(entityType))
    .then(entityAdditionalFields => {
      dispatch({ type: 'setEntityAdditionalFields', entityType, entityAdditionalFields });
      return getChangesGetData();
    })
    .catch(err => {
      console.error(err);
      notification().error({ title: getApiErrorMessage(err) });
      dispatch({ type: 'closeCrudTable' });
    });
};

export const entityChangesGetResult = <EntityChangeDataRow>(
  entityType: ActionEntityType,
  actionId: string,
): Promise<EntityChangeResultResponse<EntityChangeDataRow>> => {
  const allRows: EntityChangeResultResponse<EntityChangeDataRow>['rows'] = [];
  // TODO: try to remove any
  const callsByEntity: Partial<Record<ActionEntityType, (props: any) => any>> = {
    [ActionEntityType.PRODUCTS]: productChangesGetResult,
    [ActionEntityType.ERC_PROFILES]: ercProfilesGetResult,
    [ActionEntityType.FEES]: feeDefinitionsGetResult,
    [ActionEntityType.FEE_PROFILES]: feeProfilesGetResult,
    [ActionEntityType.EXCHANGE_RATES]: exchangeRatesGetResult,
    [ActionEntityType.BASE_RATES]: baseRatesGetResult,
    [ActionEntityType.INTEREST_RATES]: interestRatesGetResult,
    [ActionEntityType.EXCLUSIVITY_SETS]: productExclusivitySetsGetResult,
    [ActionEntityType.PROCURATION_FEE_RULES]: procurationFeeRulesGetResult,
    [ActionEntityType.PROCURATION_FEES]: procurationFeesGetResult,
    [ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS]: procurationFeeAssignmentsGetResult,
    [ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS]: firmProcFeeAssignmentsGetResult,
    [ActionEntityType.FIRMS]: firmsGetResult,
    [ActionEntityType.LENDERS]: lendersGetResult,
    [ActionEntityType.BROKERS]: intermediaryGetResult,
  };

  const getDataPage = (paginationToken?: string): Promise<EntityChangeResultResponse<EntityChangeDataRow>> =>
    callsByEntity[entityType]?.({ path: { actionId: actionId }, query: { paginationToken: paginationToken } }).then(
      ({ data }: { data: EntityChangeResultResponse<EntityChangeDataRow> }) => {
        const {
          rows,
          pageOptions: { paginationToken },
        } = data;

        allRows.push(...rows);

        return paginationToken ? getDataPage(paginationToken) : Promise.resolve({ ...data, rows: allRows });
      },
    );
  return getDataPage();
};

export const entityChangesGetData = <EntityChangeDataRow>(
  entityType: ActionEntityType,
  actionId: string,
): Promise<EntityChangeDataResponse<EntityChangeDataRow>> => {
  const allData: {
    rows: EntityChangeDataRow[];
    originalRows: EntityChangeDataRow[];
    validationFeedback: ValidationFeedback;
    isValid: boolean | null;
  } = {
    rows: [],
    originalRows: [],
    validationFeedback: {},
    isValid: null,
  };
  // TODO: try to remove any
  const callsByEntity: Partial<Record<ActionEntityType, (props: any) => any>> = {
    [ActionEntityType.PRODUCTS]: productChangesGetData,
    [ActionEntityType.ERC_PROFILES]: ercProfilesGetData,
    [ActionEntityType.FEES]: feeDefinitionsGetData,
    [ActionEntityType.FEE_PROFILES]: feeProfilesGetData,
    [ActionEntityType.EXCHANGE_RATES]: exchangeRatesGetData,
    [ActionEntityType.BASE_RATES]: baseRatesGetData,
    [ActionEntityType.INTEREST_RATES]: interestRatesGetData,
    [ActionEntityType.EXCLUSIVITY_SETS]: productExclusivitySetsGetData,
    [ActionEntityType.PROCURATION_FEE_RULES]: procurationFeeRulesGetData,
    [ActionEntityType.PROCURATION_FEES]: procurationFeesGetData,
    [ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS]: procurationFeeAssignmentsGetData,
    [ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS]: firmProcFeeAssignmentsGetData,
    [ActionEntityType.FIRMS]: firmsGetData,
    [ActionEntityType.LENDERS]: lendersGetData,
    [ActionEntityType.BROKERS]: intermediaryGetData,
  };

  const getDataPage = (paginationToken?: string): Promise<EntityChangeDataResponse<EntityChangeDataRow>> =>
    callsByEntity[entityType]?.({ path: { actionId: actionId }, query: { paginationToken: paginationToken } }).then(
      ({ data }: { data: EntityChangeDataResponse<EntityChangeDataRow> }) => {
        const {
          rows,
          originalRows,
          validationFeedback,
          pageOptions: { paginationToken },
        } = data;

        allData.rows.push(...rows);
        allData.originalRows.push(...originalRows);
        allData.validationFeedback = { ...allData.validationFeedback, ...validationFeedback };
        // Only the first page answers isValid; the pages after it send null.
        allData.isValid ??= data.isValid;

        return paginationToken ? getDataPage(paginationToken) : Promise.resolve({ ...data, ...allData });
      },
    );
  return getDataPage();
};

export const getValueListNamesByEntityType = (entityType: ActionEntityType | null): GetValueListsProps => {
  const valueListNames: Partial<Record<ActionEntityType, { fetchIfMissing?: FieldType[]; alwaysFetch?: FieldType[] }>> = {
    [ActionEntityType.PRODUCTS]: { fetchIfMissing: PRODUCTS_VALUE_LIST_NAMES, alwaysFetch: PRODUCTS_ENTITY_LIST_NAMES },
    [ActionEntityType.BASE_RATES]: { fetchIfMissing: BASE_RATES_VALUE_LIST_NAMES },
    [ActionEntityType.ERC_PROFILES]: {},
    [ActionEntityType.LENDERS]: { alwaysFetch: LENDER_ENTITY_LIST_NAMES },
    [ActionEntityType.FEES]: { fetchIfMissing: FEES_VALUE_LIST_NAMES },
    [ActionEntityType.FEE_PROFILES]: { fetchIfMissing: FEES_VALUE_LIST_NAMES, alwaysFetch: FEES_ENTITY_LIST_NAMES },
    [ActionEntityType.EXCLUSIVITY_SETS]: { alwaysFetch: EXCLUSIVE_SETS_ENTITY_LIST_NAMES },
    [ActionEntityType.FIRMS]: { alwaysFetch: FIRMS_ENTITY_LIST_NAMES },
    [ActionEntityType.BROKERS]: { fetchIfMissing: BROKERS_VALUE_LIST_NAMES },
    [ActionEntityType.PROCURATION_FEES]: { fetchIfMissing: PROCURATION_FEES_VALUE_LIST_NAMES },
    [ActionEntityType.PROCURATION_FEE_RULES]: {
      fetchIfMissing: PROCURATION_FEES_VALUE_LIST_NAMES,
      alwaysFetch: PROCURATION_FEES_ENTITY_LIST_NAMES,
    },
    [ActionEntityType.EXCHANGE_RATES]: { fetchIfMissing: EXCHANGE_RATE_VALUE_LIST_NAMES },
    [ActionEntityType.DEFAULT_PROCURATION_FEE_ASSIGNMENTS]: { fetchIfMissing: PROCURATION_FEES_LIST_NAMES },
  };
  return entityType && valueListNames[entityType] ? (valueListNames[entityType] as GetValueListsProps) : {};
};
